import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import type { PartialId } from '@attain-apps/string-builder'
import * as rxjs from 'rxjs'

import type * as kbDocumentUploads from '../../../kb-document-uploads'
import type * as me from '../../../me'
import type * as schema from '../../schema'
import type { Collection } from '../collection'
import { documentKeyFor } from '../document-key'

const SOURCE = 'DmsRecords.UploadPendingFilesOnSave.Effect'

/**
 * Minimal saved-record shape needed to locate pending file uploads. Sourced
 * from the create/update *request* rather than the API response, since the
 * `putData` mutation never returns the persisted record's values.
 */
type SavedRecord = Readonly<Pick<ents.core.dms.data.Record.Input, 'values'>> & {
  readonly recordId: ents.core.dms.data.Record.Id | undefined
}

/**
 * Once a DMS record is created or updated, uploads any Files-typed values the
 * user picked before the record was saved (staged in
 * {@link kbDocumentUploads.Feature} under a "draft" {@link kbDocumentUploads.DocumentKey}),
 * then patches the record with the resulting file names.
 */
export const uploadPendingFilesOnSave = store.effects.create(
  (
    feature: Collection['feature'],
    collection: Collection,
    kbDocumentUploadsFeature: kbDocumentUploads.Feature,
    schemaFeature: schema.Feature,
    meFeature: me.Feature,
  ) =>
    rxjs
      .merge(
        collection.plugins.dataOperationPlugin.feature.actions.create.success
          .observe()
          .pipe(
            rxjs.mergeMap(action => rxjs.from(action.data.request ?? [])),
            rxjs.map(
              (input): SavedRecord => ({
                // Not yet validated here; checked via Record.Id.isId below.
                recordId: input.values?.[ents.core.dms.data.Record.Id.KEY] as
                  | ents.core.dms.data.Record.Id
                  | undefined,
                values: input.values ?? {},
              }),
            ),
          ),
        collection.plugins.dataOperationPlugin.feature.actions.update.success
          .observe()
          .pipe(
            rxjs.mergeMap(action => rxjs.from(action.data.request ?? [])),
            rxjs.map(
              (update): SavedRecord => ({
                // Not yet validated here; checked via Record.Id.isId below.
                recordId: update.id as ents.core.dms.data.Record.Id,
                values: update.updates.values ?? {},
              }),
            ),
          ),
      )
      .pipe(
        rxjs.withLatestFrom(
          feature.selectors.context.asObservable,
          meFeature.contexts.selectors.zoneContext.asObservable,
          schemaFeature.selectors.schema.asObservable,
        ),
        rxjs.mergeMap(([entity, context, zoneContext, schemaValue]) => {
          const modelId = context?.modelId
          const recordId = entity.recordId
          // Nothing to do without knowing which model/zone this record
          // belongs to, or without a valid record id to attach uploads to.
          if (
            !modelId ||
            !zoneContext ||
            !ents.core.dms.data.Record.Id.isId(recordId)
          ) {
            return rxjs.EMPTY
          }

          // Only Files-typed attributes can have pending uploads staged.
          const filesAttributeIds = schemaValue
            .getAttributesOf(modelId)
            .filter(attribute =>
              ents.core.dms.data.Value.Type.Files.isFiles(attribute.type),
            )
            .map(
              attribute =>
                ents.core.dms.models.Id.Parts.from(attribute.id).partialId,
            )

          // Model has no Files attributes, so there's nothing to upload.
          if (!filesAttributeIds.length) {
            return rxjs.EMPTY
          }

          return _uploadPendingFilesForEntity({
            attributeIds: filesAttributeIds,
            collection,
            entity,
            kbDocumentUploadsFeature,
            modelId,
            recordId,
            target: {
              modelId,
              recordId,
              tenantId: zoneContext.tenantId,
              type: ents.core.agent.KnowledgeBase.Target.Type.Record,
              zoneId: zoneContext.zoneId,
            },
          })
        }),
      ),
)

/**
 * Uploads pending files for every Files-typed attribute on one saved record,
 * returning the actions (clearing staged selections, patching the record)
 * that need to be dispatched as a result — without dispatching them itself.
 */
function _uploadPendingFilesForEntity({
  attributeIds,
  collection,
  entity,
  kbDocumentUploadsFeature,
  modelId,
  recordId,
  target,
}: {
  readonly attributeIds: readonly PartialId[]
  readonly collection: Collection
  readonly entity: SavedRecord
  readonly kbDocumentUploadsFeature: kbDocumentUploads.Feature
  readonly modelId: ents.core.dms.models.ForRecord.Id
  readonly recordId: ents.core.dms.data.Record.Id
  readonly target: ents.core.agent.KnowledgeBase.Target.Record
}): rxjs.Observable<store.actions.Action.Constraint> {
  const results$ = attributeIds.map(attributeId =>
    _uploadPendingFilesForAttribute({
      attributeId,
      entity,
      kbDocumentUploadsFeature,
      modelId,
      recordId,
      target,
    }),
  )

  return rxjs.forkJoin(results$).pipe(
    rxjs.mergeMap(results => {
      const clearSelectionActions = results
        .map(result => result.clearSelectionAction)
        .filter((action): action is store.actions.Action.Constraint => !!action)
      const patches = results
        .map(result => result.patch)
        .filter((patch): patch is NonNullable<typeof patch> => !!patch)

      // Even if nothing ended up needing a patch (e.g. every upload failed),
      // still clear whatever selections were staged so they aren't retried.
      if (!patches.length) {
        return rxjs.from(clearSelectionActions)
      }

      const editAction = collection.plugins.editingPlugin.feature.actions.edit
        .from(SOURCE)
        .create({
          // Seed the edit from the cache. An `edit` action with no `edit`
          // callback makes the editing plugin populate its pending-update
          // state for `id` from the cached entity (the full persisted record,
          // including server-computed defaults like `!active_periods`). This
          // must be dispatched BEFORE `patchAction` below so that its callback
          // receives the seeded record rather than `undefined` - otherwise the
          // patch would submit an update containing only the files attribute,
          // wiping every untouched field from the client-side cache until the
          // next full reload.
          id: recordId,
          makeCurrent: false,
        })
      const patchAction = collection.plugins.editingPlugin.feature.actions.edit
        .from(SOURCE)
        .create({
          // `record` is the cache-seeded entity from `editAction` above.
          edit: record => {
            const next = record ?? { values: { ...entity.values } }
            next.values ??= {}
            for (const patch of patches) {
              next.values[patch.attributeId] = patch.fileNames
            }
            return next
          },
          id: recordId,
          makeCurrent: false,
        })
      const submitAction =
        collection.plugins.editingPlugin.feature.actions.submit
          .from(SOURCE)
          .create({ skipUnset: true, update: [recordId] })

      return rxjs.from([
        ...clearSelectionActions,
        editAction,
        patchAction,
        submitAction,
      ])
    }),
  )
}

/**
 * Uploads pending files staged for one Files-typed attribute, returning the
 * `clearSelection` action to run afterwards (if anything was staged) along
 * with the combined (existing + newly uploaded) file name list to patch onto
 * the record — without dispatching anything itself.
 */
function _uploadPendingFilesForAttribute({
  attributeId,
  entity,
  kbDocumentUploadsFeature,
  modelId,
  recordId,
  target,
}: {
  readonly attributeId: PartialId
  readonly entity: SavedRecord
  readonly kbDocumentUploadsFeature: kbDocumentUploads.Feature
  readonly modelId: ents.core.dms.models.ForRecord.Id
  readonly recordId: ents.core.dms.data.Record.Id
  readonly target: ents.core.agent.KnowledgeBase.Target.Record
}): rxjs.Observable<{
  readonly clearSelectionAction?: store.actions.Action.Constraint
  readonly patch?: {
    readonly attributeId: PartialId
    readonly fileNames: readonly string[]
  }
}> {
  // Matches the key `edit-record-dialog.component.ts` stages selections
  // under: the real record id (known upfront thanks to the pre-generated id
  // for Files-attribute models on create, or an already-existing id on
  // update) rather than a "draft" placeholder.
  const documentKey = documentKeyFor({
    attributeId,
    modelId,
    recordId,
  })

  return kbDocumentUploadsFeature.selectors
    .selection(documentKey)
    .asObservable.pipe(
      rxjs.take(1),
      rxjs.switchMap(pendingFiles => {
        // Nothing was staged for this attribute; no action needed at all.
        if (!pendingFiles?.length) {
          return rxjs.of({})
        }

        return rxjs
          .forkJoin(
            pendingFiles.map(file =>
              kbDocumentUploadsFeature.actions.upload
                .from(SOURCE)
                .execute({
                  file,
                  input: { name: file.name, sizeBytes: file.size, target },
                  key: `${documentKey}:${file.name}`,
                })
                .asObservable.pipe(
                  // `execute()`'s observable never completes on its own; take
                  // the first (only) response so forkJoin can complete too.
                  rxjs.take(1),
                  rxjs.map(() => file.name),
                  // A failed upload is dropped rather than failing the whole
                  // batch, so the other files can still be patched in.
                  rxjs.catchError(() => rxjs.of(undefined)),
                ),
            ),
          )
          .pipe(
            rxjs.map(uploadedNames => {
              // Staged selection was consumed (successfully or not); clear it
              // either way so it isn't re-uploaded on the next save.
              const clearSelectionAction =
                kbDocumentUploadsFeature.actions.clearSelection
                  .from(SOURCE)
                  .create({ key: documentKey })

              const newNames = uploadedNames.filter(
                (name): name is string => !!name,
              )
              // All uploads failed; nothing to patch onto the record.
              if (!newNames.length) {
                return { clearSelectionAction }
              }

              const existingNames =
                (entity.values?.[attributeId] as
                  | readonly string[]
                  | undefined) ?? []
              return {
                clearSelectionAction,
                patch: {
                  attributeId,
                  fileNames: [...existingNames, ...newNames],
                },
              }
            }),
          )
      }),
    )
}
